/**
 * La cobertura federal, medida, para el mapa público.
 *
 * Segunda puerta del sitio al backend, y la primera que no pide credencial:
 * `GET /api/curricular/jurisdictions/coverage` es una lectura pública, la
 * misma que pinta el mapa de la docente. El sitio la consulta en vez de
 * publicar una tabla escrita a mano porque el objetivo 5 no se detiene en el
 * borde del producto: una página que dice "cubierta" sobre una jurisdicción es
 * una afirmación, y una afirmación necesita una consulta atrás.
 *
 * No lanza excepciones, igual que `site-access-client`: devuelve un resultado
 * discriminado para que la UI pueda decir "no pudimos consultar la cobertura"
 * en vez de pintar un mapa vacío, que se leería como "ninguna provincia tiene
 * nada".
 */

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export interface LiveCoverageEntry {
  readonly jurisdictionId: string;
  readonly name: string;
  readonly type: string;
  readonly statements: number;
  /** Enunciados con jerarquía validada. */
  readonly structuredStatements: number;
  /** Enunciados con texto y cita, sin estructura validada (ADR-0023). */
  readonly citableStatements: number;
  /**
   * Enunciados que se ubican en un área y un año, que es la coordenada con la
   * que la docente consulta. El tier es una etiqueta del documento y esto es
   * una propiedad de la fila: Salta tiene un diseño estructurado de 1.514
   * enunciados y cuatro con año, así que el estado se deriva de este conteo.
   */
  readonly navigableStatements: number;
}

export type CoverageReading =
  | { status: 'ok'; jurisdictions: readonly LiveCoverageEntry[] }
  | { status: 'unavailable' };

function toEntry(row: unknown): LiveCoverageEntry | null {
  if (typeof row !== 'object' || row === null) return null;
  const candidate = row as Record<string, unknown>;
  const {
    jurisdictionId,
    name,
    type,
    statements,
    structuredStatements,
    citableStatements,
    navigableStatements,
  } = candidate;

  if (typeof jurisdictionId !== 'string' || typeof name !== 'string') return null;
  // Los cuatro conteos son la afirmación entera de esta pantalla, y el que
  // decide el color es `navigableStatements`. Una fila a la que le falte uno se
  // descarta: pintarla con cero diría que esa provincia no tiene nada, que es
  // distinto de no haberlo podido leer. Contra un backend viejo eso deja el
  // mapa en "no pudimos consultar la cobertura", que es lo que corresponde
  // decir cuando la medición que sostiene el color no vino.
  if (
    typeof statements !== 'number' ||
    typeof structuredStatements !== 'number' ||
    typeof citableStatements !== 'number' ||
    typeof navigableStatements !== 'number'
  ) {
    return null;
  }

  return {
    jurisdictionId,
    name,
    type: typeof type === 'string' ? type : 'provincial',
    statements,
    structuredStatements,
    citableStatements,
    navigableStatements,
  };
}

export async function fetchCoverage(): Promise<CoverageReading> {
  try {
    const response = await fetch(`${API_BASE}/api/curricular/jurisdictions/coverage`);
    if (!response.ok) return { status: 'unavailable' };

    const body = (await response.json()) as { jurisdictions?: unknown };
    if (!Array.isArray(body.jurisdictions)) return { status: 'unavailable' };

    const jurisdictions = body.jurisdictions
      .map(toEntry)
      .filter((entry): entry is LiveCoverageEntry => entry !== null);

    // Cero filas legibles sobre una respuesta 200 es una respuesta que no
    // entendemos, no un país sin currículum.
    if (jurisdictions.length === 0) return { status: 'unavailable' };

    return { status: 'ok', jurisdictions };
  } catch {
    return { status: 'unavailable' };
  }
}
